import { supabase } from "@/integrations/supabase/client";
import { LandingPageData, LandingPageResponse } from "@/models/storeModel";

export interface LandingPage {
  id: string;
  store_id: string;
  slug: string;
  title: string;
  meta_description?: string;
  meta_keywords?: string;
  og_image?: string;
  published: boolean;
  content: {
    sections: any[];
    theme_colors: {
      primary: string;
      secondary: string;
      accent: string;
    };
  };
  created_at: string;
  updated_at: string;
}

export async function createLandingPage(data: {
  store_id: string;
  slug: string;
  title: string;
  meta_description?: string;
  meta_keywords?: string;
  og_image?: string;
  published?: boolean;
  content: {
    sections: any[];
    theme_colors: {
      primary: string;
      secondary: string;
      accent: string;
    };
  };
}) {
  const { data: result, error } = await supabase
    .from("landing_pages")
    .insert(data)
    .select()
    .single();

  if (error) throw error;
  return result;
}

export async function updateLandingPage(
  id: string,
  data: Partial<Omit<LandingPageResponse, "id" | "created_at" | "updated_at">>
) {
  const { data: result, error } = await supabase
    .from("landing_pages")
    .update(data)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return result;
}

export async function getLandingPageByStoreId(storeId: string) {
  const { data, error } = await supabase
    .from("landing_pages")
    .select("*")
    .eq("store_id", storeId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function getLandingPageBySlug(slug: string) {
  const { data, error } = await supabase
    .from("landing_pages")
    .select(
      `
      *,
      stores!inner (
        name,
        address,
        phone,
        email
      )
    `
    )
    .eq("slug", slug)
    .eq("published", true)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function generateUniqueSlug(storeName: string) {
  const { data, error } = await supabase.rpc("generate_landing_page_slug", {
    store_name: storeName,
  });

  if (error) throw error;
  return data;
}

export async function uploadSectionImage(file: File, path: string) {
  const fileExt = file.name.split(".").pop();
  const fileName = `${Date.now()}-${Math.random()
    .toString(36)
    .substring(2)}.${fileExt}`;
  const fullPath = `sections/${path}/${fileName}`;

  const { data, error } = await supabase.storage
    .from("landing-pages")
    .upload(fullPath, file, {
      cacheControl: "3600",
      upsert: false,
    });

  if (error) throw error;

  const {
    data: { publicUrl },
  } = supabase.storage.from("landing-pages").getPublicUrl(fullPath);

  return publicUrl;
}

export async function deleteLandingPageImage(path: string) {
  const { error } = await supabase.storage.from("landing-pages").remove([path]);

  if (error) throw error;
}
